import type { FC, ReactElement } from 'react';
import { cloneElement, memo, useCallback, useEffect, useRef } from 'react';

import { IconFont } from '@/components/IconFont';
import { QUERY_KEYS } from '@/pages/CustomCenter/ExtractConfig/type';
import Loading from '@/loading';
import { cn } from '@/utils';
import { useQuery } from '@umijs/max';
import { useNodeDataUpdate } from '../../hooks/use-node-data-update';
import { useNodeSyncForm } from '../../hooks/use-node-sync-form';
import { useNodesInteractions } from '../../hooks/use-nodes-interactions';
import { useWorkflow, useWorkflowReadOnly } from '../../hooks/use-workflow';
import NodeIcon from '../../node-icon';
import * as API from '../../services';
import { useStore } from '../../store';
import { BaseFormExposeRef, Node, NodePanelProps } from '../../types';
import { TitleInput } from './components/title-des-input';
import { useBlurPanel } from './hooks/use-blur-panel';
import { useResizePanel } from './hooks/use-resize-panel';
import { sleep, debounce } from 'radash';
type PanelWrapperProps = {
  children: ReactElement;
} & Node;

const PanelWrapper: FC<PanelWrapperProps> = ({ id, data, children }) => {
  const template_code = useStore((s) => s.template_code);
  const { handleNodeDataUpdate } = useNodeDataUpdate();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const { handleNodeSelect } = useNodesInteractions();
  const { handleNodeDataUpdateWithSyncDraft } = useNodeDataUpdate();
  const { doSyncNodeFormDraft } = useNodeSyncForm();
  // const eventEmitter = useEventEmitter<{ type: 'NODE_CLICK' }>();
  const eventEmitter = useStore((s) => s.eventEmitter);
  const eventFromContext = useStore((s) => s.eventFromContext);
  const { nodesReadOnly } = useWorkflowReadOnly();

  const {
    data: jobData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.WORKFLOW_CONFIG,
      API.getExtractionCustomNode.queryKey,
      { node_id: id, template_code },
    ],
    queryFn: () => {
      return API.getExtractionCustomNode({ node_id: id, template_code });
    },
    enabled: Boolean(template_code) && Boolean(id),
    refetchOnWindowFocus: false,
  });

  const { panelWidth, setPanelWidth } = useWorkflow();
  const formRef = useRef<BaseFormExposeRef>(null);
  const handleResize = useCallback(
    (width: number) => {
      setPanelWidth(width);
    },
    [setPanelWidth],
  );

  const { triggerRef, containerRef } = useResizePanel({
    direction: 'horizontal',
    triggerDirection: 'left',
    minWidth: 420,
    maxWidth: 720,
    onResize: handleResize,
  });

  const handleSaveForm = useCallback(async () => {
    const res = await formRef.current?.assembleForm?.();
    if (!res) return;

    await doSyncNodeFormDraft({
      nodeId: id,
      jobData: res.form,
    });
  }, [doSyncNodeFormDraft, id]);

  const handleClosePanel = async () => {
    await handleSaveForm();
    handleNodeSelect({ nodeId: id, cancelSelection: true, sync: true });
  };

  useBlurPanel(formContainerRef, () => {
    if (nodesReadOnly) return;
    handleSaveForm();
  });

  eventEmitter?.useSubscription(async ({ type, payload }) => {
    if (nodesReadOnly) return;
    if (type === 'NODE_CLICK' && payload?.node_id === id) return;
    // console.log('eventEmitter emit NODE_CLICK event');
    await handleSaveForm();
    await sleep(1000);

    if (type === 'SWITCH_TAB') {
      payload?.resolve?.(true);
    }
  });

  eventFromContext?.useSubscription(async ({ type, payload }) => {
    if (nodesReadOnly) return;
    if (type === 'CLICK_PUBLISH') {
      console.log('eventFromContext emit CLICK_PUBLISH event');
      await handleSaveForm();
      payload?.resolve?.(true);
    }
  });

  useEffect(() => {
    const handleMouseOut = async (event: MouseEvent) => {
      await handleSaveForm();
    };

    document.addEventListener('mouseleave', handleMouseOut);

    return () => {
      document.removeEventListener('mouseleave', handleMouseOut);
    };
  }, []);

  return (
    <div className={cn('relative mr-2 h-full ')}>
      <div
        ref={triggerRef}
        className="top-1/2 -left-2 absolute w-3 h-6 -translate-y-1/2 resize-x cursor-col-resize"
      >
        <div className="bg-gray-300 hover:bg-gray-400 rounded-md w-1 h-6"></div>
      </div>

      <div
        ref={containerRef}
        className={cn(
          'h-full bg-white shadow-lg border-[0.5px] border-[#10182814] rounded-2xl',
          'overflow-y-auto',
        )}
        style={{
          width: `${panelWidth}px`,
        }}
      >
        <div className="top-0 z-10 sticky bg-white border-b-[0.5px] border-black/5 h-[60px]">
          <div className="flex items-center space-x-2 px-4 py-4 pb-1">
            <NodeIcon size="medium" type={data.type} />
            <TitleInput
              disabled={nodesReadOnly}
              value={data.title || ''}
              onBlur={(val) => {
                handleNodeDataUpdateWithSyncDraft({
                  id,
                  data: { title: val },
                });
              }}
            />
            <div className="flex items-center text-gray-500 shrink-0">
              <div
                className="flex justify-center items-center text-[16px] cursor-pointer"
                onClick={handleClosePanel}
              >
                <IconFont
                  className="hover:text-gray-600 hover:scale-110"
                  type="icon-guanbi"
                />
              </div>
            </div>
          </div>
        </div>
        <div ref={formContainerRef} className="py-2 h-[calc(100%-60px)]">
          {isLoading || isFetching ? (
            <Loading />
          ) : (
            cloneElement(children, {
              id,
              data,
              formRef: formRef,
              initialValue: jobData?.job_data,
              node_id: id,
              readonly: nodesReadOnly,
            } satisfies NodePanelProps<any>)
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(PanelWrapper);
